<script setup lang="ts">
import { IconButton, VideoCardSkeleton } from '@laam/ui/base';

import {
	type ProductContentData,
	type ProductContentSchema,
} from '@laam/cms-shared';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';
import { computed, provide } from 'vue';
import ProductVideoCard from '../../ProductVideoCard.vue';

const VideoReels = defineAsyncComponent(
	() => import('~/components/styling-videos/VideoReels.vue'),
);

interface Props {
	metadata: ProductContentData;
	data: ProductContentSchema[];
	sectionIndex: number;
}

const { data, metadata } = defineProps<Props>();
const { $eventClient } = useNuxtApp();
const getFormattedPrice = computed(() => {
	return (price: number) => {
		return `${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
	};
});

const productsContentsCarouselRef = ref();
const showReelsDialog = ref(false);
const selectedIndex = ref(0);

const componentInfo = {
	heading: 'styling-videos',
	component: 'HomepageVideoCarousel',
};

const handleNext = () => {
	productsContentsCarouselRef.value?.next();
};
const handlePrev = () => {
	productsContentsCarouselRef.value?.prev();
};

const maxSlide = computed(() => {
	return productsContentsCarouselRef.value?.data.maxSlide.value;
});

const currentSlide = computed(() => {
	return productsContentsCarouselRef.value?.data.currentSlide.value;
});
const isPrevDisabled = computed(() => {
	return currentSlide.value == 0;
});

const isNextDisabled = computed(() => {
	return currentSlide.value >= maxSlide.value;
});

const openVideoReels = (index: number) => {
	showReelsDialog.value = true;
	selectedIndex.value = index;
};

const sendClickEvent = () => {
	$eventClient.sendEvent('video-listing-click', {
		location: window.location.href,
		referrer: window.location.origin,
	});
};

provide('getFormattedPrice', getFormattedPrice);
provide('currencySymbol', 'PKR');

const { deviceType } = useDeviceType();
</script>

<template>
	<div>
		<div
			class="gap-7xl py-3xl pr-0 flex flex-col items-center"
			:class="{
				'gap-xl': deviceType === 'mobile',
			}"
		>
			<div class="flex flex-row w-full justify-between items-center px-3xl">
				<div class="gap-sm flex flex-col items-start w-full">
					<h2 class="text-lg font-semibold lg:display-xs text-gray-900">
						{{ metadata.title }}
					</h2>
					<!-- <span class="text-sm font-normal text-neutral-600">
				10 new collections in last 7 days.
			</span> -->
				</div>

				<div class="flex flex-row gap-3xl items-center">
					<NuxtLink
						:to="'/styling-videos'"
						class="text-sm font-semibold underline whitespace-nowrap cursor-pointer"
						@click="sendClickEvent"
					>
						View All
					</NuxtLink>
					<div
						v-if="deviceType !== 'mobile'"
						class="flex gap-xs w-full justify-end"
					>
						<IconButton
							variant="secondary"
							size="xs"
							rounded="full"
							:disabled="isPrevDisabled"
							@click="handlePrev"
						>
							<PhCaretLeft />
						</IconButton>
						<IconButton
							variant="secondary"
							size="xs"
							rounded="full"
							:disabled="isNextDisabled"
							@click="handleNext"
						>
							<PhCaretRight />
						</IconButton>
					</div>
				</div>
			</div>

			<div
				class="products-video-carousel--body gap-3xl flex items-center justify-center w-full pl-3xl"
			>
				<Carousel
					v-if="data"
					ref="productsContentsCarouselRef"
					class="drop-tabs--carousel w-full"
					:items-to-show="deviceType === 'mobile' ? 2.2 : 4"
					snap-align="start"
				>
					<Slide
						v-for="(item, horizontalIndex) in data"
						:key="horizontalIndex"
						class="drop-tabs--carousel__item"
						:index="horizontalIndex"
					>
						<div class="mr-xl lg:mr-7xl w-full overflow-hidden">
							<ClientOnly>
								<ProductVideoCard
									class="cursor-pointer"
									:data="item"
									:index="horizontalIndex"
									:data-source="{
										dataSource: 'valhalla',
										clickedFrom: ['homepage'],
										version: 'v0',
										parentComponent: 'products-video-carousel',
									}"
									:component-info="componentInfo"
									page="homepage"
									:horizontal-index="horizontalIndex"
									:vertical-index="sectionIndex"
									@click="openVideoReels(horizontalIndex)"
								/>
							</ClientOnly>
						</div>
					</Slide>
					<Slide :index="data.length" @click="sendClickEvent">
						<NuxtLink :to="'/styling-videos'" class="w-full h-full">
							<div class="mr-xl lg:mr-7xl aspect-[9/16] overflow-hidden h-full">
								<div
									class="bg-white border aspect-[9/16] rounded-sm p-xl flex items-center justify-center cursor-pointer hover:shadow-md transition-shadow duration-300"
								>
									<div class="flex flex-col gap-md">
										<IconButton
											variant="secondary"
											:class="'w-[56px] h-[56px]'"
											rounded="full"
										>
											<PhCaretRight />
										</IconButton>
										<span
											class="text-center text-md font-semibold text-gray-700"
										>
											View All</span>
									</div>
								</div>
							</div>
						</NuxtLink>
					</Slide>

					<!-- <template #addons>
					<Navigation
						v-if="data && data.length > 3 && deviceType !== 'mobile'"
					/>
				</template> -->
				</Carousel>
				<Carousel
					v-else
					class="w-full"
					:items-to-show="deviceType === 'mobile' ? 2.2 : 4"
					snap-align="start"
				>
					<Slide v-for="i in [0, 1, 2, 3]" :key="i">
						<VideoCardSkeleton />
					</Slide>
				</Carousel>
			</div>
			<Suspense>
				<template v-if="showReelsDialog">
					<VideoReels
						v-model:show-reels="showReelsDialog"
						:show-dialog="true"
						:homepage-data="data"
						:page-idx="0"
						:content-idx="selectedIndex"
					/>
				</template>
			</Suspense>
		</div>
	</div>
</template>

<style>
.drop-tabs--carousel .carousel__prev,
.drop-tabs--carousel .carousel__next {
	@apply bg-white rounded-full p-md mx-3xl lg:p-lg lg:mx-5xl;
}
.drop-tabs--carousel .drop-tabs--carousel__item {
	@apply relative rounded-xs text-xl text-white bg-white w-full;
}

.drop-tabs--carousel .carousel__slide {
	@apply p-none items-start;
}

.drop-tabs--carousel .carousel__pagination {
	@apply absolute mx-auto left-[0px] right-[0px] gap-sm w-fit bottom-3xl;
}

.drop-tabs--carousel .carousel__pagination-item {
	@apply box-border;
}

.drop-tabs--carousel .carousel__pagination-button {
	@apply p-none;
}

.drop-tabs--carousel .carousel__pagination-button::after {
	@apply bg-white rounded-2xl w-10xl h-xs opacity-40;
}

.drop-tabs--carousel .carousel__pagination-button--active::after {
	@apply bg-white opacity-100;
}
</style>
